import { Box, Button } from '@mui/material';
import { useInlineAsset } from '../context';
import { useMediaLibStore } from 'screens/editorial/asset/components/common/MediaLibraryUtilities/useMediaLibStore';
import { useTranslation } from 'react-i18next';
import { useAssetViewContext } from 'screens/editorial/asset/context/AssetViewContextProvider';
import { memo } from 'react';

const ASSET_TYPES_WITHOUT_SAVE = ['image', 'video', 'audio', 'pdf', 'file'];

type InlineEditButtonsProps = {
  validForm?: boolean;
  handleSaveClick: (event: React.MouseEvent<HTMLButtonElement>) => void;
};

const InlineEditButtons = memo(
  ({ handleSaveClick, validForm = true }: InlineEditButtonsProps) => {
    const { t } = useTranslation();
    const { setView, inlineProps, inlineRecord, isRelatedContent } = useInlineAsset();
    const { openRelationshipsSearchModal } = useAssetViewContext();
    const { setQueryState, setReplaceNode } = useMediaLibStore();
    const filter = inlineRecord?.type || inlineProps?.node?.attrs?.type;
    const hasUuid = Boolean(inlineRecord?.uuid || inlineRecord?.id);
    const showSaveButton = !ASSET_TYPES_WITHOUT_SAVE.includes(filter) || hasUuid;

    // Handler to open the search modal with replacement configuration
    const handleAssetLibraryButtonClick = () => {
      // Force focus on the editor so node position updates correctly
      inlineProps?.editor?.commands?.focus();

      // Get the position of the inline node
      const pos = inlineProps?.getPos();
      if (typeof pos !== 'number') {
        console.error('inlineProps.getPos() did not return a valid position.');
        return;
      }

      setQueryState((prevState) => ({
        ...prevState,
        type: [filter],
      }));
      setReplaceNode(pos);

      // Open the relationships search modal with type filter and replaceNodePosition
      openRelationshipsSearchModal({
        typeFilter: filter ? [filter] : undefined,
        replaceNodePosition: pos,
      });
    };

    return (
      <Box
        data-testid="inline-edit-buttons"
        sx={{
          display: 'flex',
          justifyContent: 'center',
          marginTop: 1.5,
          gap: 2,
        }}
      >
        {
          /** Exclude relcontent for now. Eventaully should be a drawer */
          !hasUuid && !isRelatedContent && (
            <Button
              data-testid="inline-edit-medialib-button"
              aria-label={t('blox.asset_library.label')}
              onClick={handleAssetLibraryButtonClick}
              variant="contained"
              disableElevation
            >
              {t('blox.asset_library.label')}
            </Button>
          )
        }
        {showSaveButton && (
          <Button
            data-testid="inline-edit-save-button"
            variant="contained"
            onClick={handleSaveClick}
            disabled={!validForm}
            disableElevation
          >
            {!hasUuid ? t('blox.actions.create') : t('blox.actions.update')}
          </Button>
        )}
        {hasUuid && (
          <Button
            data-testid="inline-edit-cancel-button"
            variant="contained"
            onClick={() => {
              setView('display');
            }}
            disableElevation
          >
            Cancel
          </Button>
        )}
      </Box>
    );
  },
);
InlineEditButtons.displayName = 'InlineEditButtons';

export default InlineEditButtons;
